import { accessApiKey, API_PMS_URL, authType } from "@/constants/ApiSetting";

export type GenImageUrlsSuccess = {
  upload_urls: string[];
  download_urls: string[];
};

type GenImageUrlsResponse = {
  data: {
    result_code: string;
    result_msg: string;
    msg_code: string;
    result_data: GenImageUrlsSuccess;
  };
  status: number;
  message: string;
};

/**
 * Call BE to generate presigned upload URLs and public download URLs.
 * Behavior matches the existing duplicated implementations in HotelProfile/Gallery.
 */
export async function genImageUrls(params: {
  propCode: string;
  fileNames: string[];
}): Promise<GenImageUrlsSuccess> {
  const res = await fetch(`${API_PMS_URL}/admin/hotel_profile/gen_image_urls`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${authType} ${accessApiKey}`,
    },
    body: JSON.stringify({
      prop_code: params.propCode,
      file_names: params.fileNames,
    }),
  });

  if (!res.ok) {
    throw new Error(`gen_image_urls failed: HTTP ${res.status}`);
  }

  const json = (await res.json()) as GenImageUrlsResponse;

  if (json?.data?.result_code !== "0") {
    throw new Error(
      `gen_image_urls failed: ${json?.data?.result_msg || "Unknown error"}`
    );
  }

  const result = json.data.result_data;
  if (!result?.upload_urls?.length || !result?.download_urls?.length) {
    throw new Error("gen_image_urls failed: missing upload_urls/download_urls");
  }

  return result;
}

/**
 * PUT file to a presigned URL.
 * Keeps the exact headers used previously (Content-Type + x-amz-acl).
 */
export async function putFileToPresignedUrl(params: {
  uploadUrl: string;
  file: File;
}) {
  console.log("Uploading file to presigned URL:", {
    uploadUrl: params.uploadUrl,
    fileName: params.file.name,
    fileType: params.file.type,
    fileSize: params.file.size,
  });

  const res = await fetch(params.uploadUrl, {
    method: "PUT",
    body: params.file,
    headers: {
      // บางระบบต้อง “ตรงกับที่เซ็นไว้”
      "Content-Type": params.file.type,
      "x-amz-acl": "public-read",
    },
  });

  if (!res.ok) {
    // DO Spaces/S3 มักตอบ 403 ถ้า signature mismatch / header ไม่ตรง / หมดอายุ
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed: HTTP ${res.status} ${text}`);
  }
}

/**
 * Upload a single image and return its download URL.
 * Same behavior as before: uses original filename (no timestamp) to avoid breaking BE expectations.
 */
export async function uploadSingleImageAndGetDownloadUrl(params: {
  propCode: string;
  file: File;
}): Promise<string> {
  const safeName = params.file.name;

  console.log("Uploading image with filename:", safeName);

  const { upload_urls, download_urls } = await genImageUrls({
    propCode: params.propCode,
    fileNames: [safeName],
  });

  await putFileToPresignedUrl({ uploadUrl: upload_urls[0], file: params.file });

  return download_urls[0];
}

/**
 * Optional helper: upload many files in order and return download urls.
 * (ยังไม่ต้องใช้ก็ได้ แต่อยู่ไว้ทำ gallery batch อนาคต)
 */
export async function uploadManyImagesAndGetDownloadUrls(params: {
  propCode: string;
  files: File[];
}): Promise<string[]> {
  if (!params.files.length) return [];

  const fileNames = params.files.map((f) => f.name);
  const { upload_urls, download_urls } = await genImageUrls({
    propCode: params.propCode,
    fileNames,
  });

  await Promise.all(
    params.files.map((file, idx) =>
      putFileToPresignedUrl({ uploadUrl: upload_urls[idx], file })
    )
  );

  return download_urls;
}